import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { SkeletonFormSections } from '../../components/ui/Skeleton'
import { useToast } from '../../context/ToastContext'
import { ORGANIZATION_FORM_CURRENCIES } from '../../utils/currencies'
import {
  PLATFORM_PRICING_LOCALES,
  fetchAdminPlatformPricing,
  patchAdminPlatformPricing,
  type PlatformPlanLocaleContent,
  type PlatformPricingDocument,
  type PlatformPricingPlan,
  type PlatformPricingSectionLocaleContent,
} from '../../services/platformPricingApi'

const textareaClass =
  'min-h-[4rem] w-full resize-y rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm transition-all placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30'

const LOCALE_LABELS: Record<string, string> = {
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
}

const PLAN_LABELS: Record<PlatformPricingPlan['code'], string> = {
  standard: 'Standard',
  premium: 'Premium',
  plus: 'Premium Plus',
  enterprise: 'Enterprise',
}

const SECTION_DEFAULTS: Record<string, PlatformPricingSectionLocaleContent> = {
  en: {
    kicker: 'Pricing',
    title: '',
    subtitle: '',
    note: '',
    monthSuffix: '/mo',
    yearSuffix: '/yr',
    recommendedLabel: 'Recommended',
  },
  de: {
    kicker: 'Preise',
    title: '',
    subtitle: '',
    note: '',
    monthSuffix: '/Mon.',
    yearSuffix: '/Jahr',
    recommendedLabel: 'Empfohlen',
  },
  es: {
    kicker: 'Precios',
    title: '',
    subtitle: '',
    note: '',
    monthSuffix: '/mes',
    yearSuffix: '/año',
    recommendedLabel: 'Recomendado',
  },
  fr: {
    kicker: 'Tarifs',
    title: '',
    subtitle: '',
    note: '',
    monthSuffix: '/mois',
    yearSuffix: '/an',
    recommendedLabel: 'Recommandé',
  },
}

function emptySection(locale = 'en'): PlatformPricingSectionLocaleContent {
  return { ...(SECTION_DEFAULTS[locale] ?? SECTION_DEFAULTS.en) }
}

function normalizeSectionForLocale(
  locale: string,
  content: Partial<PlatformPricingSectionLocaleContent> | undefined,
): PlatformPricingSectionLocaleContent {
  return { ...emptySection(locale), ...(content ?? {}) }
}

function emptyPlanLocale(): PlatformPlanLocaleContent {
  return { name: '', tagline: '', features: [''], cta: '' }
}

function sectionFromDoc(doc: PlatformPricingDocument | null, locale: string) {
  return normalizeSectionForLocale(locale, doc?.sectionLocales?.[locale as keyof typeof doc.sectionLocales])
}

function planLocaleFromDoc(plan: PlatformPricingPlan | undefined, locale: string) {
  return { ...emptyPlanLocale(), ...(plan?.locales?.[locale as keyof typeof plan.locales] ?? {}) }
}

function currencyAmounts(
  plan: PlatformPricingPlan,
  code: 'EUR' | 'NGN',
  documentCurrency: string,
): { priceAmount: number | null; yearlyPriceAmount: number | null } {
  const fromMap = plan.pricesByCurrency?.[code]
  if (fromMap) {
    return {
      priceAmount: fromMap.priceAmount ?? null,
      yearlyPriceAmount: fromMap.yearlyPriceAmount ?? null,
    }
  }
  if (code === documentCurrency.toUpperCase()) {
    return {
      priceAmount: plan.priceAmount ?? null,
      yearlyPriceAmount: plan.yearlyPriceAmount ?? null,
    }
  }
  return { priceAmount: null, yearlyPriceAmount: null }
}

function setPlanCurrencyAmount(
  plan: PlatformPricingPlan,
  code: 'EUR' | 'NGN',
  patch: { priceAmount?: number | null; yearlyPriceAmount?: number | null },
  documentCurrency: string,
): Partial<PlatformPricingPlan> {
  const current = currencyAmounts(plan, code, documentCurrency)
  const nextAmounts = {
    priceAmount: patch.priceAmount !== undefined ? patch.priceAmount : current.priceAmount,
    yearlyPriceAmount:
      patch.yearlyPriceAmount !== undefined ? patch.yearlyPriceAmount : current.yearlyPriceAmount,
  }
  const pricesByCurrency = {
    ...(plan.pricesByCurrency ?? {}),
    [code]: nextAmounts,
  }
  const legacy =
    code === documentCurrency.toUpperCase()
      ? {
          priceAmount: nextAmounts.priceAmount,
          yearlyPriceAmount: nextAmounts.yearlyPriceAmount,
        }
      : {}
  return { pricesByCurrency, ...legacy }
}

export default function PlatformPricingPage() {
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [doc, setDoc] = useState<PlatformPricingDocument | null>(null)
  const [activeLocale, setActiveLocale] = useState<string>('en')
  const [defaultLocale, setDefaultLocale] = useState('en')
  const [currency, setCurrency] = useState('EUR')
  const [subscriptionSettings, setSubscriptionSettings] = useState<PlatformPricingDocument['subscriptionSettings']>({
    trial: { enabled: true, durationDays: 14, eligiblePlanCodes: ['standard', 'premium'] },
    requireCardUpfront: true,
    expiry: { hardBlockStorefront: true, gracePeriodDays: 0 },
  })
  const [sectionForm, setSectionForm] = useState<PlatformPricingSectionLocaleContent>(() => emptySection())
  const [plans, setPlans] = useState<PlatformPricingPlan[]>([])
  const [planLocaleForms, setPlanLocaleForms] = useState<Record<string, PlatformPlanLocaleContent>>({})
  const [submitting, setSubmitting] = useState(false)
  const { pushToast } = useToast()

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const data = await fetchAdminPlatformPricing()
      setDoc(data)
      setDefaultLocale(data.defaultLocale || 'en')
      setCurrency(data.currency || 'EUR')
      setSubscriptionSettings(
        data.subscriptionSettings ?? {
          trial: { enabled: true, durationDays: 14, eligiblePlanCodes: ['standard', 'premium'] },
          requireCardUpfront: true,
          expiry: { hardBlockStorefront: true, gracePeriodDays: 0 },
        },
      )
      setPlans(data.plans ?? [])
    } catch (e) {
      setLoadError((e as Error).message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    setSectionForm(sectionFromDoc(doc, activeLocale))
    const next: Record<string, PlatformPlanLocaleContent> = {}
    for (const plan of plans) {
      next[plan.code] = planLocaleFromDoc(plan, activeLocale)
    }
    setPlanLocaleForms(next)
  }, [doc, activeLocale, plans])

  function updatePlan(code: PlatformPricingPlan['code'], patch: Partial<PlatformPricingPlan>) {
    setPlans((prev) => prev.map((p) => (p.code === code ? { ...p, ...patch } : p)))
  }

  function setRecommended(code: PlatformPricingPlan['code']) {
    setPlans((prev) => prev.map((p) => ({ ...p, recommended: p.code === code })))
  }

  function movePlan(code: PlatformPricingPlan['code'], dir: -1 | 1) {
    setPlans((prev) => {
      const sorted = [...prev].sort((a, b) => a.sortOrder - b.sortOrder)
      const idx = sorted.findIndex((p) => p.code === code)
      const swap = idx + dir
      if (idx < 0 || swap < 0 || swap >= sorted.length) return prev
      const a = sorted[idx]!
      const b = sorted[swap]!
      return prev.map((p) => {
        if (p.code === a.code) return { ...p, sortOrder: b.sortOrder }
        if (p.code === b.code) return { ...p, sortOrder: a.sortOrder }
        return p
      })
    })
  }

  function updatePlanLocaleForm(code: string, patch: Partial<PlatformPlanLocaleContent>) {
    setPlanLocaleForms((prev) => ({ ...prev, [code]: { ...prev[code]!, ...patch } }))
  }

  function updatePlanFeature(code: string, index: number, value: string) {
    setPlanLocaleForms((prev) => {
      const cur = prev[code] ?? emptyPlanLocale()
      const features = [...cur.features]
      features[index] = value
      return { ...prev, [code]: { ...cur, features } }
    })
  }

  function addPlanFeature(code: string) {
    setPlanLocaleForms((prev) => {
      const cur = prev[code] ?? emptyPlanLocale()
      if (cur.features.length >= 8) return prev
      return { ...prev, [code]: { ...cur, features: [...cur.features, ''] } }
    })
  }

  function removePlanFeature(code: string, index: number) {
    setPlanLocaleForms((prev) => {
      const cur = prev[code] ?? emptyPlanLocale()
      const features = cur.features.filter((_, i) => i !== index)
      return { ...prev, [code]: { ...cur, features: features.length ? features : [''] } }
    })
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    try {
      const mergedPlans = plans.map((plan) => {
        const localeContent = planLocaleForms[plan.code] ?? emptyPlanLocale()
        const features = localeContent.features.map((f) => f.trim()).filter(Boolean)
        const eur = currencyAmounts(plan, 'EUR', currency)
        const ngn = currencyAmounts(plan, 'NGN', currency)
        const pricesByCurrency = {
          EUR: {
            priceAmount:
              plan.priceType === 'contact' && !eur.priceAmount ? null : eur.priceAmount,
            yearlyPriceAmount: eur.yearlyPriceAmount,
          },
          NGN: {
            priceAmount:
              plan.priceType === 'contact' && !ngn.priceAmount ? null : ngn.priceAmount,
            yearlyPriceAmount: ngn.yearlyPriceAmount,
          },
        }
        const legacy = pricesByCurrency[currency.toUpperCase() as 'EUR' | 'NGN'] ?? pricesByCurrency.EUR
        return {
          ...plan,
          priceAmount: legacy.priceAmount,
          yearlyPriceAmount: legacy.yearlyPriceAmount,
          pricesByCurrency,
          maxStaffCount:
            plan.maxStaffCount === null || plan.maxStaffCount === undefined
              ? plan.code === 'enterprise'
                ? null
                : 0
              : Number(plan.maxStaffCount),
          locales: {
            ...plan.locales,
            [activeLocale]: {
              ...localeContent,
              features: features.length ? features : [''],
            },
          },
        }
      })

      const mergedSectionLocales = Object.fromEntries(
        Object.entries({
          ...(doc?.sectionLocales ?? {}),
          [activeLocale]: sectionForm,
        }).map(([locale, content]) => [locale, normalizeSectionForLocale(locale, content)]),
      )

      const updated = await patchAdminPlatformPricing({
        defaultLocale: defaultLocale as PlatformPricingDocument['defaultLocale'],
        currency,
        subscriptionSettings,
        sectionLocales: mergedSectionLocales,
        plans: mergedPlans,
      })
      setDoc(updated)
      setPlans(updated.plans)
      pushToast({ message: 'Pricing saved', variant: 'success' })
    } catch (err) {
      pushToast({ message: (err as Error).message || 'Save failed', variant: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 pb-10">
        <SkeletonFormSections />
      </div>
    )
  }

  if (loadError) {
    return (
      <Card className="mx-auto max-w-4xl p-6">
        <p className="text-sm text-destructive" role="alert">
          {loadError}
        </p>
        <Button type="button" className="mt-4" variant="secondary" onClick={() => void load()}>
          Retry
        </Button>
      </Card>
    )
  }

  const sortedPlans = [...plans].sort((a, b) => a.sortOrder - b.sortOrder)

  return (
    <form className="mx-auto max-w-4xl space-y-8 pb-12" onSubmit={(e) => void handleSave(e)}>
      <Card>
        <CardHeader
          title="Marketing pricing"
          description="Subscription plans shown on the Kartly marketing site homepage. Set EUR and NGN list prices per plan (no FX conversion). Copy is edited per locale."
        />
        <p className="px-6 pb-2 text-xs text-muted-foreground">
          NGN amounts are fixed list prices for Nigeria. Marketing visitors in Nigeria see NGN; everyone else sees EUR.
        </p>
        <div className="grid gap-4 px-6 pb-6 sm:grid-cols-2">
          <label className="space-y-1.5 text-sm">
            <span className="font-medium text-foreground">Default locale</span>
            <Select
              id="defaultLocale"
              label="Default locale"
              value={defaultLocale}
              onChange={(e) => setDefaultLocale(e.target.value)}
            >
              {PLATFORM_PRICING_LOCALES.map((l) => (
                <option key={l} value={l}>
                  {LOCALE_LABELS[l]}
                </option>
              ))}
            </Select>
          </label>
          <label className="space-y-1.5 text-sm">
            <span className="font-medium text-foreground">Currency</span>
            <Select
              id="pricingCurrency"
              label="Currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {ORGANIZATION_FORM_CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </Select>
          </label>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Subscription policy"
          description="Controls self-serve signup trials, billing gate, and storefront expiry."
        />
        <div className="space-y-4 px-6 pb-6">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={subscriptionSettings?.trial.enabled ?? true}
              onChange={(e) =>
                setSubscriptionSettings((s) => ({
                  ...(s ?? {
                    trial: { enabled: true, durationDays: 14, eligiblePlanCodes: ['standard', 'premium'] },
                    requireCardUpfront: true,
                    expiry: { hardBlockStorefront: true, gracePeriodDays: 0 },
                  }),
                  trial: { ...(s?.trial ?? { durationDays: 14, eligiblePlanCodes: ['standard', 'premium'] }), enabled: e.target.checked },
                }))
              }
            />
            Free trial enabled
          </label>
          <label className="block space-y-1.5 text-sm">
            <span className="font-medium text-foreground">Trial duration (days)</span>
            <Input
              type="number"
              min={0}
              max={365}
              value={subscriptionSettings?.trial.durationDays ?? 14}
              onChange={(e) =>
                setSubscriptionSettings((s) => ({
                  ...(s ?? {
                    trial: { enabled: true, durationDays: 14, eligiblePlanCodes: ['standard', 'premium'] },
                    requireCardUpfront: true,
                    expiry: { hardBlockStorefront: true, gracePeriodDays: 0 },
                  }),
                  trial: {
                    ...(s?.trial ?? { enabled: true, eligiblePlanCodes: ['standard', 'premium'] }),
                    durationDays: Number(e.target.value),
                  },
                }))
              }
            />
          </label>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-foreground">Trial eligible plans</legend>
            {(['standard', 'premium', 'plus', 'enterprise'] as const).map((code) => (
              <label key={code} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={subscriptionSettings?.trial.eligiblePlanCodes.includes(code) ?? false}
                  onChange={(e) => {
                    setSubscriptionSettings((s) => {
                      const base = s ?? {
                        trial: { enabled: true, durationDays: 14, eligiblePlanCodes: ['standard', 'premium'] as const },
                        requireCardUpfront: true,
                        expiry: { hardBlockStorefront: true, gracePeriodDays: 0 },
                      }
                      const set = new Set(base.trial.eligiblePlanCodes)
                      if (e.target.checked) set.add(code)
                      else set.delete(code)
                      return {
                        ...base,
                        trial: { ...base.trial, eligiblePlanCodes: [...set] as PlatformPricingPlan['code'][] },
                      }
                    })
                  }}
                />
                {PLAN_LABELS[code]}
              </label>
            ))}
          </fieldset>
          <label className="block space-y-1.5 text-sm">
            <span className="font-medium text-foreground">Grace period after expiry (days)</span>
            <Input
              type="number"
              min={0}
              max={90}
              value={subscriptionSettings?.expiry.gracePeriodDays ?? 0}
              onChange={(e) =>
                setSubscriptionSettings((s) => ({
                  ...(s ?? {
                    trial: { enabled: true, durationDays: 14, eligiblePlanCodes: ['standard', 'premium'] },
                    requireCardUpfront: true,
                    expiry: { hardBlockStorefront: true, gracePeriodDays: 0 },
                  }),
                  expiry: {
                    ...(s?.expiry ?? { hardBlockStorefront: true }),
                    gracePeriodDays: Number(e.target.value),
                  },
                }))
              }
            />
          </label>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        {PLATFORM_PRICING_LOCALES.map((l) => (
          <Button
            key={l}
            type="button"
            size="sm"
            variant={activeLocale === l ? 'primary' : 'secondary'}
            onClick={() => setActiveLocale(l)}
          >
            {LOCALE_LABELS[l]}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader title="Section copy" description={`Homepage pricing header (${LOCALE_LABELS[activeLocale]})`} />
        <div className="grid gap-4 px-6 pb-6">
          {(
            [
              ['kicker', 'Kicker'],
              ['title', 'Title'],
              ['subtitle', 'Subtitle'],
              ['note', 'Footnote'],
              ['monthSuffix', 'Month suffix'],
              ['yearSuffix', 'Year suffix'],
              ['recommendedLabel', 'Recommended badge'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="space-y-1.5 text-sm">
              <span className="font-medium text-foreground">{label}</span>
              <Input
                value={sectionForm[key]}
                onChange={(e) => setSectionForm((prev) => ({ ...prev, [key]: e.target.value }))}
              />
            </label>
          ))}
        </div>
      </Card>

      {sortedPlans.map((plan) => {
        const form = planLocaleForms[plan.code] ?? emptyPlanLocale()
        return (
          <Card key={plan.code} padding={false} className="overflow-hidden">
            <div className="border-b border-border px-6 py-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">{PLAN_LABELS[plan.code]}</h3>
                  <p className="text-xs text-muted-foreground">Code: {plan.code}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" size="sm" variant="secondary" onClick={() => movePlan(plan.code, -1)}>
                    Up
                  </Button>
                  <Button type="button" size="sm" variant="secondary" onClick={() => movePlan(plan.code, 1)}>
                    Down
                  </Button>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={plan.active}
                      onChange={(e) => updatePlan(plan.code, { active: e.target.checked })}
                    />
                    Active
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="recommended-plan"
                      checked={plan.recommended}
                      onChange={() => setRecommended(plan.code)}
                    />
                    Recommended
                  </label>
                </div>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <label className="space-y-1.5 text-sm">
                  <span className="font-medium">Price type</span>
                  <Select
                    id={`${plan.code}-priceType`}
                    label="Price type"
                    value={plan.priceType}
                    onChange={(e) =>
                      updatePlan(plan.code, {
                        priceType: e.target.value as PlatformPricingPlan['priceType'],
                      })
                    }
                  >
                    <option value="monthly">Monthly</option>
                    <option value="from">From (prefix + amount)</option>
                    <option value="contact">Contact / custom</option>
                  </Select>
                </label>
                <label className="space-y-1.5 text-sm">
                  <span className="font-medium">Monthly price (EUR)</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={currencyAmounts(plan, 'EUR', currency).priceAmount ?? ''}
                    onChange={(e) =>
                      updatePlan(
                        plan.code,
                        setPlanCurrencyAmount(
                          plan,
                          'EUR',
                          {
                            priceAmount: e.target.value === '' ? null : Number(e.target.value),
                          },
                          currency,
                        ),
                      )
                    }
                    placeholder={plan.priceType === 'contact' ? 'Optional' : 'Required'}
                  />
                </label>
                <label className="space-y-1.5 text-sm">
                  <span className="font-medium">Monthly price (NGN)</span>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={currencyAmounts(plan, 'NGN', currency).priceAmount ?? ''}
                    onChange={(e) =>
                      updatePlan(
                        plan.code,
                        setPlanCurrencyAmount(
                          plan,
                          'NGN',
                          {
                            priceAmount: e.target.value === '' ? null : Number(e.target.value),
                          },
                          currency,
                        ),
                      )
                    }
                    placeholder={
                      plan.priceType === 'monthly'
                        ? 'Required for Nigeria'
                        : plan.priceType === 'contact'
                          ? 'Optional'
                          : 'Optional if not sold in NG'
                    }
                  />
                </label>
                {plan.priceType === 'monthly' ? (
                  <>
                    <label className="space-y-1.5 text-sm">
                      <span className="font-medium">Yearly price (EUR)</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={currencyAmounts(plan, 'EUR', currency).yearlyPriceAmount ?? ''}
                        onChange={(e) =>
                          updatePlan(
                            plan.code,
                            setPlanCurrencyAmount(
                              plan,
                              'EUR',
                              {
                                yearlyPriceAmount:
                                  e.target.value === '' ? null : Number(e.target.value),
                              },
                              currency,
                            ),
                          )
                        }
                        placeholder="Leave empty to disable yearly billing"
                      />
                    </label>
                    <label className="space-y-1.5 text-sm">
                      <span className="font-medium">Yearly price (NGN)</span>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={currencyAmounts(plan, 'NGN', currency).yearlyPriceAmount ?? ''}
                        onChange={(e) =>
                          updatePlan(
                            plan.code,
                            setPlanCurrencyAmount(
                              plan,
                              'NGN',
                              {
                                yearlyPriceAmount:
                                  e.target.value === '' ? null : Number(e.target.value),
                              },
                              currency,
                            ),
                          )
                        }
                        placeholder="Leave empty to disable yearly billing"
                      />
                    </label>
                    <label className="space-y-1.5 text-sm">
                      <span className="font-medium">Max staff seats</span>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={plan.maxStaffCount ?? ''}
                        onChange={(e) =>
                          updatePlan(plan.code, {
                            maxStaffCount:
                              e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                        placeholder="0 = owner only"
                      />
                      <p className="text-xs text-muted-foreground">
                        Staff accounts only (org owner not counted). Empty = unlimited.
                      </p>
                    </label>
                  </>
                ) : null}
              </div>
            </div>
            <div className="grid gap-4 px-6 py-6">
              <label className="space-y-1.5 text-sm">
                <span className="font-medium">Plan name</span>
                <Input
                  value={form.name}
                  onChange={(e) => updatePlanLocaleForm(plan.code, { name: e.target.value })}
                />
              </label>
              <label className="space-y-1.5 text-sm">
                <span className="font-medium">Tagline</span>
                <textarea
                  className={textareaClass}
                  value={form.tagline}
                  onChange={(e) => updatePlanLocaleForm(plan.code, { tagline: e.target.value })}
                />
              </label>
              {(plan.priceType === 'from' || plan.priceType === 'contact') && (
                <label className="space-y-1.5 text-sm">
                  <span className="font-medium">Price prefix (e.g. From, Ab)</span>
                  <Input
                    value={form.pricePrefix ?? ''}
                    onChange={(e) => updatePlanLocaleForm(plan.code, { pricePrefix: e.target.value })}
                  />
                </label>
              )}
              <div className="space-y-2">
                <span className="text-sm font-medium text-foreground">Features</span>
                {form.features.map((feature, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={feature}
                      onChange={(e) => updatePlanFeature(plan.code, i, e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => removePlanFeature(plan.code, i)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="secondary" size="sm" onClick={() => addPlanFeature(plan.code)}>
                  Add feature
                </Button>
              </div>
              <label className="space-y-1.5 text-sm">
                <span className="font-medium">CTA label</span>
                <Input
                  value={form.cta}
                  onChange={(e) => updatePlanLocaleForm(plan.code, { cta: e.target.value })}
                />
              </label>
            </div>
          </Card>
        )
      })}

      <div className="flex justify-end">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save pricing'}
        </Button>
      </div>
    </form>
  )
}
